// src/subscreens/mechanicadmin/profile/MechanicProfile.jsx

import React, { useState, useRef, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiEdit2, FiPhone, FiMail, FiMapPin, FiShield,
  FiUser, FiSave, FiTool, FiClock, FiPlus, FiX, FiCheck, FiCamera, FiCheckCircle, FiLock, FiBell, FiDollarSign
} from "react-icons/fi";
import { FaWrench, FaTractor } from "react-icons/fa";
import MechanicAdminLayout from "../../../layout/mechanic/MechanicMasterLayout";
import "../../../assets/css/mechanic/profile/Profile.css";
import AppContext from "../../../context/AppContext";
import axios from "axios";
import Swal from "sweetalert2";

export default function MechanicProfile() {
  const { baseUrl, baseUrlForFile, isMechanicVerified } = useContext(AppContext);
  const [showKycModal, setShowKycModal] = useState(false);
  const navigate = useNavigate();
  const fileRef = useRef(null);
  // ── Profile States ──
  const [fullname, setFullname] = useState("");
  const [email, setEmail] = useState("");
  const [specialization, setSpecialization] = useState("");
  const [image, setImage] = useState(null);
  const [phonenumber, setPhonenumber] = useState("");
  const [location, setLocation] = useState("");
  const [yearsofexperience, setYearsofexperience] = useState("");
  const [expertise, setExpertise] = useState("");
  const [bookingfee, setBookingfee] = useState("");

  // ── UI States ──
  const [editMode, setEditMode] = useState(false);
  const [saveMsg, setSaveMsg] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [tagError, setTagError] = useState("");
  const [avatar, setAvatar] = useState(null);

  // ── Change Password States ──
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // ── Notification States ──
  const [emailNotification, setEmailNotification] = useState(true);
  const [inAppNotification, setInAppNotification] = useState(true);

  // ── Fetch Mechanic Profile ──
  const fetchMechanicProfile = () => {
    const token = localStorage.getItem("authToken");
    axios
      .get(`${baseUrl}/mechanicadmin/profile/fetchMechanicProfile`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.data.success === true) {
          setFullname(response.data.mechanic.fullname);
          setEmail(response.data.mechanic.email);
          setPhonenumber(response.data.mechanic.phonenumber);
          setLocation(response.data.mechanic.location);
          setYearsofexperience(response.data.mechanic.yearsofexperience);
          setSpecialization(response.data.mechanic.specialization);
          setImage(response.data.mechanic.image);
          setAvatar(
            response.data.mechanic.image
              ? `${baseUrlForFile}/${response.data.mechanic.image}`
              : null
          );
          setExpertise(response.data.mechanic.expertise);
          setBookingfee(response.data.mechanic.bookingfee ?? "");
          setEmailNotification(response.data.mechanic.emailNotification == 1);
          setInAppNotification(response.data.mechanic.inAppNotification == 1);
        }
      })
      .catch((error) => console.log(error));
  };

  // ── Update Mechanic Profile ──
  const updateMechanicProfile = () => {
    const token = localStorage.getItem("authToken");
    const formData = new FormData();
    formData.append("fullname", fullname);
    formData.append("email", email);
    formData.append("phonenumber", phonenumber);
    formData.append("location", location);
    formData.append("image", image);
    formData.append("yearsofexperience", yearsofexperience);
    formData.append("expertise", expertise);
    formData.append("specialization", specialization);
    formData.append("bookingfee", bookingfee);
    axios
      .post(`${baseUrl}/mechanicadmin/profile/updateMechanicProfile`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.data.success === true) {
          fetchMechanicProfile();
          setEditMode(false);
          setSaveMsg(true);
          setTimeout(() => setSaveMsg(false), 3000);
        }
      })
      .catch((error) => console.log(error));
  };

  // ── Change Password ──
  const ChangePassword = () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      Swal.fire({ icon: "warning", title: "Missing Fields", text: "Please fill all password fields!" });
      return;
    }
    if (newPassword !== confirmPassword) {
      Swal.fire({ icon: "warning", title: "Mismatch", text: "Passwords do not match!" });
      return;
    }

    const token = localStorage.getItem("authToken");
    const formData = new FormData();
    formData.append("currentPassword", currentPassword);
    formData.append("newPassword", newPassword);

    axios
      .post(`${baseUrl}/mechanicadmin/profile/changePassword`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.data.success === true) {
          Swal.fire({ icon: "success", title: "Success", text: "Password changed successfully!" });
          setCurrentPassword("");
          setNewPassword("");
          setConfirmPassword("");
          setShowPasswordModal(false);
        } else {
          Swal.fire({ icon: "error", title: "Error", text: response.data.message || "Failed to change password." });
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to change password." });
      });
  };

  // ── Change Notification Setting ──
  const changeNotificationSetting = () => {
    const token = localStorage.getItem("authToken");
    const formData = new FormData();
    formData.append("emailNotification", emailNotification ? 1 : 0);
    formData.append("inAppNotification", inAppNotification ? 1 : 0);

    axios
      .post(`${baseUrl}/mechanicadmin/profile/changeNotificationSetting`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.data.success === true) {
          Swal.fire({ icon: "success", title: "Success", text: "Notification settings updated!" });
        } else {
          Swal.fire({ icon: "error", title: "Error", text: response.data.message || "Failed to update notification settings." });
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to update notification settings." });
      });
  };

  useEffect(() => {
    fetchMechanicProfile();
  }, []);

  // ── Avatar Preview ──
  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setAvatar(ev.target.result);
    reader.readAsDataURL(file);
  };

  // ── Stars renderer ──
  const renderStars = (rating) =>
    Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={i < Math.floor(rating) ? "mp-star filled" : "mp-star empty"}>★</span>
    ));

  return (
    <MechanicAdminLayout>
      <div className="mp-page">

        {/* ── Toast ── */}
        {saveMsg && (
          <div className="mp-toast">
            <FiCheck size={15} /> Profile updated successfully!
          </div>
        )}

        {/* ── Hero Banner ── */}
        <div className="mp-hero">
          <div className="mp-hero-decor mp-decor-1" />
          <div className="mp-hero-decor mp-decor-2" />
          <div className="mp-hero-decor mp-decor-3" />

          <div className="mp-avatar-wrap">
            <div className="mp-avatar">
              {avatar
                ? <img src={avatar} alt="profile" className="mp-avatar-img" />
                : <FiUser size={50} />}
            </div>
            <button className="mp-cam-btn" onClick={() => fileRef.current?.click()} title="Change photo">
              <FiCamera size={12} />
            </button>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleAvatarChange} />
          </div>

          <h2 className="mp-name">{fullname}</h2>
          <p className="mp-role">
            <FaWrench size={12} className="mp-role-icon" />
            {specialization}
          </p>
          <div className="mp-hero-location">
            <FiMapPin size={12} /> {location}
          </div>
        </div>

        {/* ── Edit / Save / Change Password Buttons ── */}
        {!editMode ? (
          <div className="mp-btn-row">
            <button className="mp-edit-btn" onClick={() => {
              if (!isMechanicVerified) {
                setShowKycModal(true);  // KYC modal open karo
              } else {
                setEditMode(true);
              }
            }}>
              <FiEdit2 size={15} /> Edit Profile
            </button>
            <button className="mp-cancel-btn" onClick={() => setShowPasswordModal(true)}>
              <FiLock size={15} /> Change Password
            </button>
          </div>
        ) : (
          <div className="mp-btn-row">
            <button className="mp-save-btn" onClick={updateMechanicProfile}>
              <FiSave size={15} /> Save Changes
            </button>
            <button className="mp-cancel-btn" onClick={() => { setEditMode(false); fetchMechanicProfile(); }}>
              <FiX size={15} /> Cancel
            </button>
          </div>
        )}

        {/* ── Info Card ── */}
        <div className="mp-card">

          {/* Full Name */}
          {editMode && (
            <div className="mp-row">
              <div className="mp-row-icon"><FiUser /></div>
              <div className="mp-row-body">
                <label className="mp-lbl">Full Name</label>
                <input className="mp-input" value={fullname} onChange={(e) => setFullname(e.target.value)} placeholder="Your full name" />
              </div>
            </div>
          )}

          {/* Specialization */}
          {editMode && (
            <div className="mp-row">
              <div className="mp-row-icon"><FiTool /></div>
              <div className="mp-row-body">
                <label className="mp-lbl">Speciality</label>
                <input className="mp-input" value={specialization} onChange={(e) => setSpecialization(e.target.value)} placeholder="e.g. Agricultural Equipment Expert" />
              </div>
            </div>
          )}

          {/* Phone */}
          <div className="mp-row">
            <div className="mp-row-icon"><FiPhone /></div>
            <div className="mp-row-body">
              {editMode ? (
                <><label className="mp-lbl">Phone Number</label>
                  <input className="mp-input" value={phonenumber} onChange={(e) => setPhonenumber(e.target.value)} placeholder="+92 300 1234567" /></>
              ) : (
                <span className="mp-val">{phonenumber}</span>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="mp-row">
            <div className="mp-row-icon"><FiMail /></div>
            <div className="mp-row-body">
              {editMode ? (
                <><label className="mp-lbl">Email Address</label>
                  <input className="mp-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" /></>
              ) : (
                <span className="mp-val">{email}</span>
              )}
            </div>
          </div>

          {/* Location */}
          <div className="mp-row">
            <div className="mp-row-icon"><FiMapPin /></div>
            <div className="mp-row-body">
              {editMode ? (
                <><label className="mp-lbl">Location</label>
                  <input className="mp-input" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="City, Province" /></>
              ) : (
                <span className="mp-val">{location}</span>
              )}
            </div>
          </div>

          {/* Years of Experience */}
          {editMode && (
            <div className="mp-row">
              <div className="mp-row-icon"><FiClock /></div>
              <div className="mp-row-body">
                <label className="mp-lbl">Years of Experience</label>
                <input className="mp-input" type="number" min="0" value={yearsofexperience} onChange={(e) => setYearsofexperience(e.target.value)} placeholder="e.g. 5" />
              </div>
            </div>
          )}

          {/* KYC */}
          <div className="mp-row mp-row-last">
            <div className="mp-row-icon mp-icon-green"><FiShield /></div>
            <div className="mp-row-body">
              <span className="mp-val mp-kyc">KYC Verified <span className="mp-kyc-tick">✓</span></span>
            </div>
          </div>

        </div>

        {/* ── Expertise Card ── */}
        <div className="mp-card mp-expertise-card">
          <div className="mp-expertise-header">
            <div className="mp-expertise-title-wrap">
              <div className="mp-expertise-title-icon"><FaTractor size={15} /></div>
              <h4 className="mp-expertise-title">My Expertise</h4>
            </div>
          </div>

          {!editMode ? (
            <div className="mp-expertise-tags">
              {expertise && expertise.split(",").map((tag, i) => (
                <span className="mp-expertise-tag" key={i}>{tag.trim()}</span>
              ))}
            </div>
          ) : (
            <div className="mp-expertise-edit">
              <div className="mp-tag-add-row">
                <input
                  className="mp-input mp-tag-input"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (tagInput.trim()) {
                        setExpertise(prev => prev ? `${prev},${tagInput.trim()}` : tagInput.trim());
                        setTagInput("");
                      }
                    }
                  }}
                  placeholder="e.g. Hydraulic Pump Repair"
                />
                <button className="mp-tag-add-btn" onClick={() => {
                  if (tagInput.trim()) {
                    setExpertise(prev => prev ? `${prev},${tagInput.trim()}` : tagInput.trim());
                    setTagInput("");
                  }
                }}>
                  <FiPlus size={16} />
                </button>
              </div>
              <p className="mp-tag-hint">Press Enter or click + to add a tag</p>
              <div className="mp-expertise-tags">
                {expertise && expertise.split(",").map((tag, i) => (
                  <span className="mp-expertise-tag mp-expertise-tag-edit" key={i}>
                    {tag.trim()}
                    <button className="mp-tag-remove" onClick={() => {
                      const arr = expertise.split(",").filter((_, idx) => idx !== i);
                      setExpertise(arr.join(","));
                    }}>
                      <FiX size={10} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Booking Fee Card ── */}
        <div className="mp-card mp-fee-card">
          <div className="mp-expertise-header">
            <div className="mp-expertise-title-wrap">
              <div className="mp-expertise-title-icon"><FiDollarSign size={15} /></div>
              <h4 className="mp-expertise-title">Booking Fee</h4>
            </div>
          </div>

          {!editMode ? (
            <div className="mp-fee-display">
              <span className="mp-fee-amount">
                Rs. {bookingfee ? bookingfee : 0}
              </span>
              <span className="mp-fee-caption">Per Booking</span>
            </div>
          ) : (
            <div className="mp-fee-edit">
              <label className="mp-lbl">Set Your Booking Fee (Rs.)</label>
              <input
                className="mp-input"
                type="number"
                min="0"
                value={bookingfee}
                onChange={(e) => setBookingfee(e.target.value)}
                placeholder="e.g. 500"
              />
              <p className="mp-tag-hint">This is the amount a customer pays per booking request</p>
            </div>
          )}
        </div>

        {/* ── Notification Preferences Card ── */}
        <div className="mp-card mp-notif-card">
          <div className="mp-expertise-header">
            <div className="mp-expertise-title-wrap">
              <div className="mp-expertise-title-icon"><FiBell size={15} /></div>
              <h4 className="mp-expertise-title">Notification Preferences</h4>
            </div>
          </div>

          <div className="mp-notif-row">
            <div className="mp-notif-info">
              <div className="mp-notif-icon"><FiMail /></div>
              <div className="mp-notif-text">
                <span className="mp-notif-label">Email Notifications</span>
                <span className="mp-notif-desc">Receive updates via email</span>
              </div>
            </div>
            <label className="mp-toggle">
              <input
                type="checkbox"
                checked={emailNotification}
                onChange={() => setEmailNotification(!emailNotification)}
              />
              <span className="mp-toggle-slider"></span>
            </label>
          </div>

          <div className="mp-notif-row">
            <div className="mp-notif-info">
              <div className="mp-notif-icon"><FiBell /></div>
              <div className="mp-notif-text">
                <span className="mp-notif-label">In-App Notifications</span>
                <span className="mp-notif-desc">Receive updates inside the platform</span>
              </div>
            </div>
            <label className="mp-toggle">
              <input
                type="checkbox"
                checked={inAppNotification}
                onChange={() => setInAppNotification(!inAppNotification)}
              />
              <span className="mp-toggle-slider"></span>
            </label>
          </div>

          <button className="mp-notif-save-btn" onClick={changeNotificationSetting}>
            <FiSave size={15} /> Save Preferences
          </button>
        </div>

      </div>

      {/* ── KYC Modal ── */}
      {showKycModal && (
        <div className="kyc-overlay" onClick={() => setShowKycModal(false)}>
          <div className="kyc-modal" onClick={(e) => e.stopPropagation()}>
            <button className="kyc-close-btn" onClick={() => setShowKycModal(false)}>
              <FiX size={16} />
            </button>
            <div className="kyc-top">
              <div className="kyc-icon-wrap">
                <FiCheckCircle size={34} color="#fff" />
              </div>
              <h2 className="kyc-title">Complete Your Verification</h2>
              <p className="kyc-subtitle">
                To edit your profile on <strong className="kyc-brand">AgriRent</strong>, please
                complete your KYC verification first.
              </p>
              <div className="kyc-benefits-box">
                <p className="kyc-benefits-heading">Benefits of Verification:</p>
                {[
                  "Accept maintenance requests from farmers",
                  "Receive payments directly to your account",
                  "Get verified mechanic badge on your profile",
                  "Build trust with farmers across Pakistan",
                ].map((benefit, i) => (
                  <div className="kyc-benefit-item" key={i}>
                    <div className="kyc-benefit-check"><FiCheckCircle size={14} /></div>
                    <span className="kyc-benefit-text">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="kyc-bottom">
              <button
                className="kyc-verify-btn"
                onClick={() => { setShowKycModal(false); navigate("/mechanic-admin/kyc"); }}
              >
                Verify Now
              </button>
              <p className="kyc-note">
                You can complete verification anytime from Settings → Documents
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Change Password Modal ── */}
      {showPasswordModal && (
        <div className="kyc-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="kyc-modal" onClick={(e) => e.stopPropagation()}>
            <button className="kyc-close-btn" onClick={() => setShowPasswordModal(false)}>
              <FiX size={16} />
            </button>
            <div className="kyc-top">
              <div className="kyc-icon-wrap">
                <FiLock size={30} color="#fff" />
              </div>
              <h2 className="kyc-title">Change Password</h2>
            </div>

            <div style={{ padding: "0 24px 24px" }}>
              <label className="mp-lbl">Current Password</label>
              <input
                type="password"
                className="mp-input"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                style={{ marginBottom: "12px" }}
              />

              <label className="mp-lbl">New Password</label>
              <input
                type="password"
                className="mp-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                style={{ marginBottom: "12px" }}
              />

              <label className="mp-lbl">Confirm New Password</label>
              <input
                type="password"
                className="mp-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                style={{ marginBottom: "16px" }}
              />

              <button className="kyc-verify-btn" onClick={ChangePassword}>
                Change Password
              </button>
            </div>
          </div>
        </div>
      )}

    </MechanicAdminLayout>
  );
}